import React, { useEffect, useState, useRef, useContext } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box, Flex, Grid, Link, Spinner } from "theme-ui";
import { useNavigate, useParams } from "react-router-dom";
import { themevals } from "theme/themevals";

import { deleteClient, getClient, updateClient, getClients } from "api/Clients";

import { CountryStates } from "helpers/CountryStates";
import { filterByKeys } from "helpers/default";
import SelectSearch from "components/forms/SelectSearch";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { BackArrowIcon, CheckMarkIcon, QuestionMarkIcon, TrashIcon } from "components/svg";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import Separator from "components/ui/Separator";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import Checkbox from "components/forms/Checkbox";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

export default function ClientCreate() {
  const countryStates: any[] = Object.entries(CountryStates["US"]?.states || {}).map(([key, value]) => ({ value: key, label: value.name }));

  const toast = useToast();
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);
  const initialClientValues = useRef<any>(null);

  const [client, setClient] = useState<any>(null);

  // Helper to find actually changed fields by comparing initial and current values
  const getChangedFields = (initialValues: any, currentValues: any, keys: string[]) => {
    const changed: string[] = [];
    
    keys.forEach(key => {
      // Handle nested objects like 'address'
      if (key.includes('.')) {
        const [parent, child] = key.split('.');
        if (initialValues[parent]?.[child] !== currentValues[parent]?.[child]) {
          changed.push(key);
        }
      } else if (key === 'address') {
        // Compare entire address object
        const initialAddr = JSON.stringify(initialValues.address || {});
        const currentAddr = JSON.stringify(currentValues.address || {});
        if (initialAddr !== currentAddr) {
          changed.push(key);
        }
      } else {
        // Direct comparison for simple fields
        if (initialValues[key] !== currentValues[key]) {
          changed.push(key);
        }
      }
    });
    
    return changed;
  };

  const submitUpdate = async (values: any) => {
    const keys = ["category", "name", "firstName", "lastName", "website", "phone", "address", "contactEmail", "billingEmail", "companyAdmin", "terms", "requiresDeposit", "depositPercentage", "portalLocationsEnabled", "portalCalculatorEnabled", "portalCalculatorAccess", "portalUserManagementEnabled", "excludeFromReporting", "exclusionReason"];
    const update = filterByKeys(keys, values);
    if (update.portalUserManagementEnabled !== undefined && update.portalUserManagementEnabled !== null) {
      update.portalUserManagementEnabled = update.portalUserManagementEnabled === true || Number(update.portalUserManagementEnabled) === 1;
    }
    // Stored as 1/NULL, the API takes a boolean.
    if (update.portalCalculatorAccess !== undefined && update.portalCalculatorAccess !== null) {
      update.portalCalculatorAccess = update.portalCalculatorAccess === true || Number(update.portalCalculatorAccess) === 1;
    } else {
      delete update.portalCalculatorAccess;
    }

    console.log("Update Client", update);
    console.log("Initial Values (from ref):", initialClientValues.current);
    console.log("Current Values:", values);

    const res = await updateClient({ data: { id: params.id }, update });
    if (res?.id) {
      toast.add("Client updated.", "var(--theme-ui-colors-success)");
      
      // Get only the fields that actually changed
      const changedFields = initialClientValues.current 
        ? getChangedFields(initialClientValues.current, values, keys)
        : [];
      console.log("Changed Fields:", changedFields);
      
      // Track client update only if there were actual changes
      if (changedFields.length > 0) {
        void trackActivity({
          module: "clients",
          action: "update_client",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "client",
          entityId: res.id,
          metadata: {
            updatedFields: changedFields,
            clientType: values.type === 1 ? "individual" : "company",
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    } else {
      toast.add(res?.message ? res.message : "Error updating client", "var(--theme-ui-colors-danger)");
    }
  };

  const _validate = Yup.object().shape({
    type: Yup.number().required("Type is required."),
    name: Yup.string().when("type", { is: (v: number) => v === 2, then: (schema) => schema.required("This field is required."), otherwise: (schema) => schema.nullable() }),
    //website: Yup.string().when("type", {is: (v: number) => v === 2, then: (schema) => schema.required('This field is required.'), otherwise: (schema) => schema.nullable()}),
    firstName: Yup.string().when("type", { is: (v: number) => v === 1, then: (schema) => schema.required("This field is required."), otherwise: (schema) => schema.nullable() }),
    lastName: Yup.string().when("type", { is: (v: number) => v === 1, then: (schema) => schema.required("This field is required."), otherwise: (schema) => schema.nullable() }),
    contactEmail: Yup.string().required("This field is required."),
  });

  const initClient = async () => {
    const res = await getClient({ data: { id: params.id } });
    setClient(res?.id ? res : null);
    
    // Store initial values for comparison
    if (res?.id) {
      initialClientValues.current = {
        ...res,
        parentId: res.parent?.id,
        firstName: res.primaryUser?.firstName,
        lastName: res.primaryUser?.lastName
      };
    }
    
    // Track view after successful load
    if (res?.id && !hasTrackedView.current) {
      hasTrackedView.current = true;
      void trackActivity({
        module: "clients",
        action: "view_client_edit",
        actionType: "view",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "client",
        entityId: res.id,
        metadata: {},
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    }
  };

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const confirmDelete = async () => {
    setConfirmSubmit(true);
    const res = await deleteClient({ data: client });
    if (res?.success) {
      toast.add("Client deactivated.", "var(--theme-ui-colors-success)");
      navigate("/clients");
    } else {
      toast.add(res?.message ? res.message : "Error deleting client.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const submitDelete = async () => {
    setConfirmModal(
      <BasicModal
        title="Deactivate client?"
        size={"sm"}
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <Flex sx={{ gap: "10px" }}>
            <BasicButton type="button" scheme="danger" onClick={() => confirmDelete()} $submitting={confirmSubmit} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
              {!confirmSubmit && <TrashIcon fill="#fff" />}
              {"Deactivate"}
            </BasicButton>
          </Flex>
        }
      >
        <P>
          Are you sure you want to deactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{client?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Client";
    initClient();
  }, []);

  if (!client)
    return (
      <Box sx={{ textAlign: "center", paddingY: "1.5rem" }}>
        <Spinner />
      </Box>
    );

  return (
    <>
      <Header></Header>
      <Page
        title="Edit client"
        subtitle="Edit a client and their details."
        backAction={
          <Link variant="back" href={`/clients/${params.id}`}>
            <BackArrowIcon />
            Back to Client
          </Link>
        }
      >
        <Formik
          initialValues={{ ...client, parentId: client.parent?.id, firstName: client.primaryUser?.firstName, lastName: client.primaryUser?.lastName }}
          onSubmit={submitUpdate}
          validationSchema={_validate}
          enableReinitialize
        >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            return (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px", flexGrow: 1 }}>
                      <BasicButton scheme="dangerSecondary" styles={{ marginRight: "auto!important" }} type="button" onClick={() => submitDelete()}>
                        <TrashIcon fill={"#A60000"} /> Deactivate
                      </BasicButton>
                      <Link href="/clients" sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none" }}>
                        Cancel
                      </Link>
                      <BasicButton type="submit" styles={{ margin: "0px 0 0px 10px" }} $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Client
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={2} columns={[1, "1fr 2fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit Client Name and Phone.
                        </Subtext>
                      </Box>
                      <Box>
                        <Grid gap={3} columns={[1, 3]}>
                          <SelectInput
                            name="type"
                            label="Client Type"
                            value={values.type}
                            options={[
                              { value: 1, label: "Individual" },
                              { value: 2, label: "Company" },
                            ]}
                            onChange={(val: any) => {
                              if (val === 1) {
                                setFieldValue("name", "");
                              }
                              setFieldValue("type", val);
                            }}
                            disabled
                            $errors={errors.type && submitCount > 0 ? errors.type : null}
                          />
                          <SelectInput
                            name="category"
                            label="Client Category"
                            value={values.category}
                            options={[
                              { value: "Builder", label: "Builder" },
                              { value: "Owner", label: "Owner" },
                              { value: "Manager", label: "Manager" },
                            ]}
                            onChange={(val: any) => setFieldValue("category", val)}
                            $errors={errors.category && submitCount > 0 ? errors.category : null}
                            disabled={values.parentId !== undefined ? true : false}
                          />
                          {values.type === 1 ? (
                            <SelectSearch
                              api={getClients}
                              params={{
                                filters: { type: 2 },
                              }}
                              multiple={false}
                              placeholder="None"
                              name="parentId"
                              label="Company Name"
                              value={values?.parentId}
                              keyLabel={["name"]}
                              keyValue="id"
                              preload
                              onChange={(val: any) => {
                                setFieldValue("parentId", val?.id);
                                console.log("SelectSearch onChange triggered with:", val);
                                console.log("val type:", typeof val);
                                console.log("val keys:", val ? Object.keys(val) : "val is null/undefined");
                                setFieldValue("category", val?.category);
                              }}
                              $errors={errors?.["parentId"] && submitCount > 0 ? errors?.["parentId"] : null}
                            />
                          ) : (
                            <TextInput
                              name="name"
                              label="Company Name"
                              placeholder={values.type === 1 ? "None" : ""}
                              value={values.type === 1 ? "" : values.name}
                              onChange={(val: any) => setFieldValue("name", val)}
                              $errors={errors.name && submitCount > 0 ? errors.name : null}
                              disabled={values.type === 1}
                            />
                          )}
                        </Grid>
                        <Grid gap={3} columns={[1, 3]}>
                          {values.type === 1 ? (
                            <>
                              <TextInput
                                name="firstName"
                                label="First Name"
                                value={values?.firstName}
                                onChange={(val: any) => setFieldValue("firstName", val)}
                                $errors={errors?.firstName && submitCount > 0 ? errors?.firstName : null}
                              />
                              <TextInput
                                name="lastName"
                                label="Last Name"
                                value={values?.lastName}
                                onChange={(val: any) => setFieldValue("lastName", val)}
                                $errors={errors?.lastName && submitCount > 0 ? errors?.lastName : null}
                              />
                            </>
                          ) : (
                            <>
                              <TextInput
                                name="website"
                                label="Website"
                                value={values?.website}
                                onChange={(val: any) => setFieldValue("website", val)}
                                $errors={errors?.website && submitCount > 0 ? errors?.website : null}
                                $customStyles={{ gridColumn: [1, "span 2"] }}
                              />
                            </>
                          )}
                          <TextInput
                            name="phone"
                            label="Phone"
                            value={values.phone}
                            onChange={(val: any) => setFieldValue("phone", val)}
                            $errors={errors?.phone && submitCount > 0 ? errors?.phone : null}
                          />
                        </Grid>
                        {values.parentId !== undefined && (
                          <Grid gap={3} columns={[1, 1]}>
                            <Checkbox
                              label="Grant Admin access"
                              checked={values.companyAdmin}
                              labelSize="14px"
                              sublabel="Admins have full access to manage users and settings."
                              onChange={(val) => {
                                setFieldValue("companyAdmin", val);
                              }}
                            />
                            {!values.companyAdmin && (
                              <Checkbox
                                label="Mulch Calculator"
                                checked={values.portalCalculatorAccess === true || Number(values.portalCalculatorAccess) === 1}
                                labelSize="14px"
                                sublabel="Lets this person use the Mulch Calculator. Their organization must also have it switched on; admins always have it."
                                onChange={(val) => {
                                  setFieldValue("portalCalculatorAccess", val);
                                }}
                              />
                            )}
                          </Grid>
                        )}
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={2} columns={[1, "1fr 2fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Address
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit Client Address.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ gap: "1rem" }}>
                          <TextInput
                            name="address[line1]"
                            label={"Address 1"}
                            value={values?.address?.line1}
                            onChange={(val: any) => setFieldValue("address.line1", val)}
                            $customStyles={{ flexGrow: 2 }}
                          />
                          <TextInput
                            name="address[line2]"
                            label="Address 2"
                            value={values?.address?.line2}
                            onChange={(val: any) => setFieldValue("address.line2", val)}
                            $customStyles={{ flexGrow: 1 }}
                          />
                        </Flex>
                        <Flex sx={{ gap: "1rem" }}>
                          <TextInput
                            name="address[city]"
                            label="City"
                            value={values?.address?.city}
                            onChange={(val: any) => setFieldValue("address.city", val)}
                            $customStyles={{ width: "33.3333%" }}
                          />
                          <SelectInput
                            name="address[state]"
                            label="State"
                            value={values?.address?.state}
                            options={countryStates}
                            onChange={(val: any) => setFieldValue("address.state", val)}
                            clearable
                            $customStyles={{ width: "33.3333%" }}
                          />
                          <TextInput name="address[postalCode]" label="Zip" value={values?.address?.postalCode} onChange={(val: any) => setFieldValue("address.postalCode", val)} $customStyles={{ width: "33.3333%" }} />
                        </Flex>
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={2} columns={[1, "1fr 2fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Email
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit preferred emails for the client.
                        </Subtext>
                      </Box>
                      <Box>
                        <Grid gap={3} columns={[1, 2]}>
                          <TextInput
                            type="email"
                            name="contactEmail"
                            label="Contact Email"
                            value={values?.contactEmail}
                            onChange={(val: any) => setFieldValue("contactEmail", val)}
                            $errors={errors?.contactEmail && submitCount > 0 ? errors?.contactEmail : null}
                          />
                          <TextInput
                            type="email"
                            name="billingEmail"
                            label="Billing Email"
                            value={values?.billingEmail}
                            onChange={(val: any) => setFieldValue("billingEmail", val)}
                            $errors={errors?.billingEmail && submitCount > 0 ? errors?.billingEmail : null}
                          />
                        </Grid>
                      </Box>
                    </Grid>
                    {values.type === 2 && (
                      <>
                        <Separator />
                        <Grid gap={2} columns={[1, "1fr 2fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                          <Box>
                            <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                              Terms
                            </H5>
                            <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                              Edit terms for a Client Company.
                            </Subtext>
                          </Box>
                          <Box>
                            <Grid gap={3} columns={[1, 3]}>
                              <SelectInput
                                name="terms"
                                label="Terms"
                                value={values.terms}
                                options={[
                                  { value: 30, label: "30 Days" },
                                  { value: 60, label: "60 Days" },
                                  { value: 90, label: "90 Days" },
                                ]}
                                onChange={(val: any) => setFieldValue("terms", val)}
                                $errors={errors.terms && submitCount > 0 ? errors.terms : null}
                              />
                            </Grid>
                            {/* N2 - per-client deposit requirement. When on,
                                this client's newly accepted quotes require a
                                deposit and the client is NOT offered the
                                early-payment discount. Percentage editable
                                only while the switch is on; defaults to 50. */}
                            <Box marginTop={"18px"}>
                              <Checkbox
                                label="Require a deposit on new quotes"
                                checked={values.requiresDeposit === true}
                                labelSize="14px"
                                sublabel="Newly accepted quotes for this client require a deposit. Deposit clients are not offered the early-payment discount."
                                onChange={(val) => {
                                  setFieldValue("requiresDeposit", val);
                                  if (val && (values.depositPercentage === null || values.depositPercentage === undefined)) {
                                    setFieldValue("depositPercentage", 50);
                                  }
                                }}
                              />
                            </Box>
                            {values.requiresDeposit === true && (
                              <Box marginTop={"12px"} sx={{ maxWidth: "200px" }}>
                                <TextInput
                                  name="depositPercentage"
                                  label="Deposit percentage (%)"
                                  type="number"
                                  value={values.depositPercentage ?? 50}
                                  onChange={(val: any) => {
                                    const n = val === "" || val === null ? null : Number(val);
                                    setFieldValue("depositPercentage", n);
                                  }}
                                  $errors={errors.depositPercentage && submitCount > 0 ? errors.depositPercentage : null}
                                />
                              </Box>
                            )}
                            {/* N1 - per-client portal access. ONE control:
                                "Mulch Calculator". Turning it on enables the
                                calculator AND the Locations page (the
                                calculator page only shows recent maps + a
                                create option, so a client needs the
                                Locations page to reach their other sites -
                                the two are not independent). We keep both
                                backend flags in step: the single checkbox
                                sets both, so locations is never on without
                                the calculator or vice versa. Default OFF. */}
                            <Box marginTop={"22px"}>
                              <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"8px"} fontSize={"14px"}>
                                Client portal
                              </H5>
                              <Box marginTop={"10px"}>
                                <Checkbox
                                  label="Mulch Calculator"
                                  checked={values.portalCalculatorEnabled === true}
                                  labelSize="14px"
                                  sublabel="Gives this client the Mulch Calculator and their own Locations page (their sites, their maps, and snapshots attached to their quotes or invoices)."
                                  onChange={(val) => {
                                    // One control drives both flags so they
                                    // can never be set to a broken combination.
                                    setFieldValue("portalCalculatorEnabled", val);
                                    setFieldValue("portalLocationsEnabled", val);
                                  }}
                                />
                              </Box>
                              <Box marginTop={"10px"}>
                                <Checkbox
                                  label="Client admins manage their own users"
                                  checked={values.portalUserManagementEnabled === true || Number(values.portalUserManagementEnabled) === 1}
                                  labelSize="14px"
                                  sublabel="Gives this organization's admins a Users page to add, edit and remove their people and grant the Mulch Calculator. Turn on only once the organization's admin is confirmed. Off: GetMulch manages their users."
                                  onChange={(val) => setFieldValue("portalUserManagementEnabled", val)}
                                />
                              </Box>
                            </Box>
                            {/* Item 24 - reporting exclusion. Deliberately
                                LAST and visually separated: it is not a
                                capability like the two above, it is a
                                statement about whether this organization's
                                numbers are real. Copy says "reports"
                                explicitly and says what does NOT change,
                                because the dangerous misreading is that
                                this hides or disables the client. */}
                            <Box marginTop={"22px"} paddingTop={"18px"} sx={{ borderTop: "1px solid #E5E5E5" }}>
                              <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"8px"} fontSize={"14px"}>
                                Reporting
                              </H5>
                              <Box marginTop={"10px"}>
                                <Checkbox
                                  label="Exclude from reports and metrics"
                                  checked={values.excludeFromReporting === true}
                                  labelSize="14px"
                                  sublabel="Leaves this client out of dashboards, revenue, AR and other figures. Nothing else changes - quotes, projects, invoices, the portal and the Zoho sync all keep working normally. Use it for test accounts, demos, internal records, duplicates, and organizations kept only for history."
                                  onChange={(val) => {
                                    setFieldValue("excludeFromReporting", val);
                                    if (!val) {
                                      // Turning it back on for reporting
                                      // clears the reason - a stale one
                                      // reads as though it is still excluded.
                                      setFieldValue("exclusionReason", "");
                                    }
                                  }}
                                />
                              </Box>
                              {values.excludeFromReporting === true && (
                                <Box marginTop={"12px"} sx={{ maxWidth: "420px" }}>
                                  <TextInput
                                    name="exclusionReason"
                                    label="Reason (optional)"
                                    value={values.exclusionReason ?? ""}
                                    onChange={(val: any) => setFieldValue("exclusionReason", val ?? "")}
                                    $errors={errors.exclusionReason && submitCount > 0 ? errors.exclusionReason : null}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Tab>
                </Tabs>
              </Form>
            );
          }}
        </Formik>
        {confirmModal}
      </Page>
    </>
  );
}
